import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, IonSelect, ModalController, PopoverController } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Logger } from 'src/app/api/helper/app-error-logger';
import { AuftragHelper } from 'src/app/api/helper/auftrag-helper';
import { Adresse, ArbeitsartKonfiguration, AuftragsKomponente } from 'src/app/api/model/swagger-model';
import { Auftragdetails } from '../../auftragdetails.helper';
import { KomponenteBase } from '../komponente';
import { Utils } from 'src/app/api/helper/utils';
import { AuftragService } from 'src/app/api/auftrag.service';
import { AppConfig } from 'src/app/api/helper/app.config';
import { UiHelper } from 'src/app/api/helper/ui-helper';
import { DomSanitizer } from '@angular/platform-browser';
import { Zeiterfassung } from 'src/app/api/helper/zeiterfassung-helper';

import moment from 'moment';

const log = new Logger("AuftragZeiterfassungKomponenteComponent");

@Component({
    selector: 'auftrag-zeiterfassung-komponente',
    templateUrl: './auftrag-zeiterfassung-komponente.component.html',
    styleUrls: ['./auftrag-zeiterfassung-komponente.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AuftragZeiterfassungKomponenteComponent extends KomponenteBase implements OnInit, OnChanges, OnDestroy {

    @ViewChild('artWaehlenSelect', { read: IonSelect, static: false }) artWaehlenSelect: IonSelect;

    @Input() auftragdetails: Auftragdetails;
    @Input() komponente: AuftragsKomponente;

    @Output() lieferantChanged: EventEmitter<Adresse> = new EventEmitter();

    isModalSichtbar = false;

    aktiveZeit = new BehaviorSubject<string>('');

    intervalId: any;

    zeiterfassungAktiv = AppConfig.current.einstellungen.AuftragZeiterfassungAktiv == 'ja';

    manuellBearbeitenErlaubt = AppConfig.current.einstellungen.AuftragZeiterfassungManuellBearbeiten === 'ja';
    isBearbeitenErlaubt = false;

    arbeitszeitArtenListe = AppConfig.current.arbeitszeitKonfiguration?.AuftragArbeitszeitArten;

    isAnsichtEinfach = AppConfig.current.einstellungen.AuftragZeiterfassungTyp === 'Einfach';
    isAnsichtMitArt = AppConfig.current.einstellungen.AuftragZeiterfassungTyp === 'MitArt';

    arbeitszeitenList: ArbeitszeitListItem[] = [];

    zeiterfassungLaeuft = false;

    constructor(
        private router: Router,
        private modalController: ModalController,
        private auftragService: AuftragService,
        private popoverController: PopoverController,
        private alertController: AlertController,
        public domSanitizer: DomSanitizer,
        protected cdr: ChangeDetectorRef) {
        super(cdr);
    }

    ngOnInit() {
        super.ngOnInit();
        this.init();

        this.aktualisieren();

        this.intervalId = setInterval(() => this.aktualisiereAktiveZeit(), 1000);

        this.subscriptions.push(this.auftragdetails.detectChanges.subscribe(() => {
            this.updateBearbeitenErlaubt();
            this.cdr.detectChanges();
        }));

        this.subscriptions.push(Zeiterfassung.changed.subscribe(() => {
            this.aktualisieren();
            this.cdr.detectChanges();
        }));
    }

    init() {
        if (!this.auftrag) {
            return;
        }

        this.arbeitszeitArtenListe = Zeiterfassung.getArbeitszeitArten('Auftrag');

        this.updateBearbeitenErlaubt();
    }

    aktualisieren() {
        let zeiten = Zeiterfassung.getAuftragszeiten(this.auftrag);

        this.zeiterfassungLaeuft = !!zeiten.find(p => !p.EndDatum);

        // let auftragZeiten xxx
        this.aktualisiereAktiveZeit(true);
    }

    ngOnDestroy() {
        log?.debug('ngOnDestroy');

        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.init();
    }

    updateBearbeitenErlaubt() {
        this.isBearbeitenErlaubt = false;

        if (!this.auftrag) {
            return;
        }

        if (AuftragHelper.isAuftragImArchiv(this.auftrag)) {
            return;
        }

        if (this.auftragdetails.isReadOnly) {
            return;
        }

        this.isBearbeitenErlaubt = true;
    }

    async onStart() {
        log?.debug('onStart: ' + this.auftrag.Key);

        // Prüfe ob noch die Zeiterfassung von einem anderen Auftrag läuft. Wenn ja, dann muss diese erst gestoppt werden
        if (AppConfig.current.einstellungen.AuftragZeiterfassungNurEinAktiverAuftrag === "ja") {
            const ok = await Zeiterfassung.beendeLaufendeZeiterfassungMitRueckfrage();

            if (!ok) {
                return false;
            }
        }

        const typ = AppConfig.current.einstellungen.AuftragZeiterfassungTyp;

        if (typ === 'MitArt') {
            this.cdr.detectChanges();

            this.artWaehlenSelect.value = null;

            setTimeout(() => {
                this.artWaehlenSelect.open();
            });
        } else {
            // Einfach
            await this.auftragdetails.starteZeiterfassung();

            this.aktualisieren();
        }
    }

    async onArbeitszeitArtSelected(e) {
        log?.debug('onArbeitszeitArtSelected', e);

        const value = e.detail.value as ArbeitsartKonfiguration;

        if (value) {
            await this.auftragdetails.starteZeiterfassung(value);

            this.aktualisieren();
        }
    }

    async onPause() {
        log?.debug('onPause: ' + this.auftrag.Key);

        await this.auftragdetails.unterbrecheZeiterfassung();

        // this.tourStatus.Stoerzeiten.push({
        //     Guid: uuid(),
        //     ErfassungsDatum: Utils.nowIsoDateString(),
        //     Typ: 'Pause',
        //     Name: 'Pause',
        //     Dauer: 0,
        //     Key: 'AKTIVEPAUSE'
        // })

        // await this.tourService.speichereAktuelleTour();

        this.aktualisieren();
    }

    async onBearbeiten(item: ArbeitszeitListItem = null) {
        log?.info('onStoerzeitAendern');

        await this.auftragService.showAuftragZeiterfassungModal(this.auftrag, item.key);

        this.aktualisieren();
    }

    async onEnde() {
        log?.debug('onEnde: ' + this.auftrag.Key);

        await Zeiterfassung.beendeLaufendeZeiterfassungOhneRueckfrage();

        this.aktualisieren();
    }

    aktualisiereAktiveZeit(force = false) {
        if (!this.auftrag) {
            return;
        }

        let zeiten = Zeiterfassung.getAuftragszeiten(this.auftrag);

        if (this.isAnsichtMitArt) {
            // Je Art gruppieren
            let newArbeitszeitenList: ArbeitszeitListItem[] = [];

            const gruppen = Utils.groupBy(zeiten, "ArtKey");

            for (const key in gruppen) {
                if (Object.prototype.hasOwnProperty.call(gruppen, key)) {
                    const zeiten = gruppen[key];

                    const aktivDauerSekunden = AuftragHelper.BerechneAktiveDauerInSekundenFuerAuftragszeiten(zeiten);

                    const duration = moment.duration(aktivDauerSekunden, 'seconds');

                    const strAktiveZeit = ("0" + Math.floor(duration.asHours())).slice(-2) + ':' + ("0" + duration.minutes()).slice(-2) + ':' + ("0" + duration.seconds()).slice(-2).toString();

                    const listItem: ArbeitszeitListItem = {
                        key: key == 'undefined' ? null : key,
                        name: zeiten[0].ArtName,
                        bemerkung: zeiten[0].Bemerkung,
                        aktiveZeit: strAktiveZeit,
                        laeuft: !!zeiten.find(p => !p.EndDatum),
                        style: '',
                        bemerkungErforderlich: false
                    };

                    if (Utils.isBlank(listItem.bemerkung)) {
                        // Prüfe ob die Bemerkung als Pflichtfeld konfiguriert ist
                        const art = AppConfig.current.arbeitszeitKonfiguration?.AuftragArbeitszeitArten?.find(p => p.Key == key);

                        if (art && art.BemerkungPflicht) {
                            listItem.bemerkungErforderlich = true;
                        }
                    }

                    if (listItem.laeuft) {
                        if (AppConfig.current.darkMode) {
                            listItem.style = '--ion-item-background: #4d4d4d;'
                        } else {
                            listItem.style = '--ion-item-background: #A9DDB1;'
                        }
                    }

                    newArbeitszeitenList.push(listItem);
                }
            }

            // Die Liste nur aktualisieren und nicht komplett neu aufgabauen. Außer es ist erforderlich
            let rebuildErforderlich = force;

            if (newArbeitszeitenList.length == this.arbeitszeitenList.length) {
                for (let i = 0; i < this.arbeitszeitenList.length; i++) {
                    const p1 = this.arbeitszeitenList[i];
                    const p2 = newArbeitszeitenList[i];

                    if (p1.key == p2.key && p1.name == p2.name) {
                        p1.aktiveZeit = p2.aktiveZeit
                        p1.laeuft = p2.laeuft
                        p1.style = p2.style
                    } else {
                        rebuildErforderlich = true;
                        break;
                    }
                }
            } else {
                rebuildErforderlich = true;
            }

            if (rebuildErforderlich) {
                this.arbeitszeitenList = newArbeitszeitenList;
            }
        } else {
            // Einfache Summe
            const aktivDauerSekunden = AuftragHelper.BerechneAktiveDauerInSekunden(this.auftrag);

            const duration = moment.duration(aktivDauerSekunden, 'seconds');

            const strAktiveZeit = ("0" + Math.floor(duration.asHours())).slice(-2) + ':' + ("0" + duration.minutes()).slice(-2) + ':' + ("0" + duration.seconds()).slice(-2).toString();

            this.aktiveZeit.next(strAktiveZeit);
        }


        this.cdr.detectChanges();
    }

    async onArbeitszeitSelected(listItem: ArbeitszeitListItem) {
        log?.debug('onArbeitszeitSelected', listItem);

        if (this.auftragdetails.istAuftragAbgeschlossen || !listItem) {
            return;
        }

        let aktion = await this.frageArbeitszeitAktion(listItem);

        switch (aktion) {
            case 'starten':
                this.onZeiterfassungErneutStart(listItem);
                break;

            case 'unterbrechen':
                this.onPause();
                break;

            case 'loeschen':
                this.onLoeschen(listItem);
                break;

            case 'bearbeiten':
                this.onBearbeiten(listItem);
                break;
        }
    }

    async onZeiterfassungErneutStart(listItem: ArbeitszeitListItem) {
        if (!listItem?.name) {
            return;
        }

        const art = this.arbeitszeitArtenListe.find(p => p.Name == listItem.name);

        if (art) {
            await this.auftragdetails.starteZeiterfassung(art);

            this.aktualisieren();
        }
    }

    async onLoeschen(listItem: ArbeitszeitListItem) {
        const ok = await UiHelper.confirmJaNein('Eintrag wirklich löschen?');

        if (!ok) {
            return;
        }

        await Zeiterfassung.loescheAuftragZeiterfassungenByArtKey(this.auftrag, listItem.key);

        this.aktualisieren();

        this.auftragdetails.updateAuftragReadOnly();

        this.auftragdetails.detectChanges.next('Zeiterfassung.onLoeschen');
    }


    frageArbeitszeitAktion(listItem: ArbeitszeitListItem): Promise<string> {
        return new Promise(async resolve => {
            const buttons: any[] = [];

            if (this.isBearbeitenErlaubt && !listItem.laeuft) {
                buttons.push({
                    text: 'Starten',
                    handler: () => {
                        resolve('starten');
                    }
                });
            }

            // if (listItem.laeuft) {
            //     buttons.push({
            //         text: 'Zeiterfassung stoppen',
            //         handler: () => {
            //             resolve('unterbrechen');
            //         }
            //     });
            // }

            if (this.isBearbeitenErlaubt) {
                buttons.push({
                    text: 'Bearbeiten',
                    handler: () => {
                        resolve('bearbeiten');
                    }
                });

                buttons.push({
                    text: 'Löschen',
                    role: 'destructive',
                    handler: () => {
                        resolve('loeschen');
                    }
                });
            }

            if (!buttons.length) {
                resolve(null);
                return;
            }

            buttons.push({
                text: 'Abbruch',
                role: 'cancel',
                handler: () => {
                    resolve(null);
                }
            });

            let cssClass: string;

            if (buttons.length > 2) {
                cssClass = 'no-text-alert no-title-alert';
            } else {
                cssClass = 'no-text-alert';
            }

            const alert = await this.alertController.create({
                header: listItem.name,
                cssClass: cssClass,
                mode: 'ios',
                buttons: buttons
            });

            await alert.present();
        });
    }
}

interface ArbeitszeitListItem {
    key: string;
    name: string;
    aktiveZeit: string;
    laeuft: boolean;
    style: string;
    bemerkung: string;
    bemerkungErforderlich: boolean;
}